import React, { useRef } from 'react';

import { useScrollState } from '../../../helpers/hooks/useScrollState';
import { PageIndicator } from '../components/PageIndicator';
import { getTotalPages } from '../ContinuousCarousel/helpers/getTotalPages';

/*
  This carousel is designed to be used where we have icons instead of cards, where we do not see the edge of
  the next card and so we need the dots underneath to give a clear indication that this is indeed a carousel
  and has further items you can swipe to. An example of this is the BragBar.

  Do not use this carousel on a desktop screen - there are no buttons!
*/

interface IProps {
  children: React.ReactNode;
  id: string;
  className?: string;
  smallIndicatorSpacing?: boolean;
}

function getCurrentPage(
  scrollPosition: number,
  containerRef: React.RefObject<HTMLDivElement | null>,
): number {
  return containerRef && containerRef.current
    ? Math.ceil(
        (scrollPosition + containerRef.current.clientWidth) / containerRef.current.clientWidth,
      )
    : 1;
}

const scrollToPage = (page: number, containerRef: React.RefObject<HTMLDivElement | null>): void => {
  const { current } = containerRef;
  if (current) {
    const pageWidth = current.clientWidth;
    current.scrollTo({ left: (page - 1) * pageWidth, behavior: 'smooth' });
  }
};

export const ContinuousCarouselDots = ({
  children,
  id,
  className = '',
  smallIndicatorSpacing = false,
}: IProps) => {
  // We pass the children as deps so that the hook triggers any time the children change.
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollPosition } = useScrollState(containerRef);
  const totalPages = getTotalPages(containerRef);
  const currentPage = getCurrentPage(scrollPosition, containerRef);

  return (
    <div className={`${className} flex flex-col`}>
      <div
        ref={containerRef}
        className="carousel-container scrollbar-none flex snap-x overflow-y-hidden scroll-smooth"
      >
        {children}
      </div>

      <PageIndicator
        currentPage={currentPage}
        totalPages={totalPages}
        onClick={page => {
          scrollToPage(page, containerRef);
        }}
        id={id}
        smallIndicatorSpacing={smallIndicatorSpacing}
      />
    </div>
  );
};
